import React, { createContext, useState, useCallback } from 'react';
import { experiencesService } from '../services/experiencesService';
import { getPromoterService } from '../services/getPromoterService';
import { AvailabilityScheduleResponse, ProductDetail } from './viator-product-types';
import { Promoter } from '../types/promoter';

export type AgeBand = NonNullable<Experience['product']['pricingInfo']['ageBands'][number]['ageBand']>;

export interface PlanItem {
  date: string;
  activities: string[];
  description: string;
  title: string;
  day: number;
  accommodation?: string;
}

export type Experience = {
  id: string;
  aliasUrl: string;
  promoter: string;
  title: string;
  type: string;
  thumbnailURL: string;
  slider: string[];
  product_id: string;
  description?: string;
  secondary_description?: string;
  product_description?: string;
  product: {
    logistics: ProductDetail['logistics'];
    bookingQuestions: ProductDetail['bookingQuestions'],
    pricingInfo: ProductDetail['pricingInfo'],
    additionalInfo: ProductDetail['additionalInfo'],
    bookingRequirements: ProductDetail['bookingRequirements'],
    cancellationPolicy: ProductDetail['cancellationPolicy'],
    languageGuides: ProductDetail['languageGuides'],
    productOptions: ProductDetail['productOptions'],
  }
  availabilitySchedule: AvailabilityScheduleResponse
  included: string[];
  notIncluded: string[];
  plan: PlanItem[];
  gallery: string[];
  font?: string;
  colors: {
    primary_color: string;
    secondary_color: string;
  };
  price: {
    promoter_commission: number
  },
}

export const DataExperienceContext = createContext<{
  promoter: Promoter | null,
  experience: Experience | null,
  loading: boolean,
  error: any | null,
  fetchData: (id: string) => Promise<void>,
}>({
  promoter: null,
  experience: null,
  fetchData: async () => { },
  loading: true,
  error: null
});

export const DataExperienceProvider = ({ children }: { children: React.ReactNode }) => {
  const [experience, setExperience] = useState<Experience | null>(null);
  const [promoter, setPromoter] = useState<Promoter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);


  const fetchData = useCallback(async (experienceId: string) => {
    setLoading(true);
    setError(null);
    try {
      const promoterData = await getPromoterService();
      const experienceData = await experiencesService.getExperience(experienceId as string);
      setExperience(experienceData);
      setPromoter(promoterData);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }

  }, []);

  return (
    <DataExperienceContext.Provider value={{ experience, fetchData, promoter, loading, error }}>
      {children}
    </DataExperienceContext.Provider>
  );
};
